import type { GlobalAccessibilityLabels as GlobalAccessibilityLabelsSchema } from '../../../amplienceTypes/schemas/imported/global-accessibility-labels-schema';
import type { PersonalizePricingExperience } from '../../../amplienceTypes/schemas/imported/personalized-pricing-experience-schema.d.ts';
import type { PageTypes } from '../../constants';
import type { PropsWithChildren } from 'react';

import { createContext, use, useCallback, useEffect, useMemo } from 'react';

import { formatNumberByMask } from '../../helpers';
import { useExperienceContext } from '../dynamic/ExperienceOptionsProvider';
import type { GlobalFormErrorLabels } from '~/amplienceTypes/schemas/imported/global-form-error-labels-schema';
import type { GlobalNotifications } from '~/amplienceTypes/schemas/imported/global-notifications-schema';
import type { GlobalQuickViewType, GlobalTopLevelNavigationType } from '~/types/amplience';
import type { ConfigurationOverrides, HybrisPageProps } from '~/types/props';
import { pushToAppSettings } from '~/utilities/helpers/syncGlobalObject';

const getCurrentHourDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const date = now.getDate().toString().padStart(2, '0');
  const hours = now.getHours().toString().padStart(2, '0');

  return new Date(`${year}-${month}-${date} ${hours}:00:00`);
};

type StaticContextProps = HybrisPageProps & {
  quickViewContent?: GlobalQuickViewType;
  topLevelNavigationContent?: GlobalTopLevelNavigationType;
  accessibilityLabels?: GlobalAccessibilityLabelsSchema;
  pageType: PageTypes;
  formErrorLabels?: GlobalFormErrorLabels;
  notifications?: GlobalNotifications;
  ppeContent?: PersonalizePricingExperience[];
};

type StaticContextState = {
  priceFormatter: (value: number, currency?: string) => string;
  configuration: ConfigurationOverrides;
};

const StaticContext = createContext<
  (Omit<StaticContextProps, 'configuration'> & StaticContextState) | undefined
>(undefined);

export const useStaticContext = () => {
  const context = use(StaticContext);

  if (context === undefined) {
    throw new Error('`useStaticContext()` is used outside of a `<StaticContextProvider />');
  }

  return context;
};

export const StaticContextProvider = ({
  configuration,
  leftNavigation,
  socialMediaChannels,
  quickViewContent,
  topLevelNavigationContent,
  navLinks,
  countryEn,
  accessibilityLabels,
  formErrorLabels,
  notifications,
  children,
  pageType,
  ppeContent,
}: PropsWithChildren<StaticContextProps>) => {
  const hourPreciseCurrentDate = getCurrentHourDate().getTime().toString();

  const {
    currentCurrencySymbol,
    currentCurrencyNumberFormat,
    staticFiltersQuery,
    userSegmentMappings,
  } = configuration;
  const {
    experienceOptions: { tk51 },
  } = useExperienceContext();

  const tk51TestConfiguration = useMemo(() => {
    if (tk51?.experienceName && configuration) {
      return {
        ...configuration,
        algoliaProductPrimaryIndexName: `${configuration.algoliaProductPrimaryIndexName}_style`,
      };
    }

    return configuration;
  }, [configuration, tk51?.experienceName]);

  const priceFormatter = useCallback(
    (priceVal: number, currency?: string) =>
      `${currency ?? currentCurrencySymbol} ${formatNumberByMask(
        priceVal,
        currentCurrencyNumberFormat || '#,##0.00'
      )}`,
    [currentCurrencySymbol, currentCurrencyNumberFormat]
  );

  const value = useMemo(
    () => ({
      configuration: {
        ...(tk51?.experienceName ? tk51TestConfiguration : configuration),
        staticFiltersQuery: staticFiltersQuery?.replaceAll('{currentDate}', hourPreciseCurrentDate),
        userSegmentMappings: userSegmentMappings?.reduce((acc, item) => {
          if (item) {
            acc[item.key] = item.value;
          }

          return acc;
        }, {} as Record<string, string>),
      },
      leftNavigation,
      socialMediaChannels,
      quickViewContent,
      topLevelNavigationContent,
      navLinks,
      countryEn,
      accessibilityLabels,
      formErrorLabels,
      pageType,
      notifications,
      ppeContent,
      priceFormatter,
    }),
    [
      configuration,
      tk51TestConfiguration,
      tk51?.experienceName,
      staticFiltersQuery,
      hourPreciseCurrentDate,
      userSegmentMappings,
      leftNavigation,
      socialMediaChannels,
      quickViewContent,
      topLevelNavigationContent,
      navLinks,
      countryEn,
      accessibilityLabels,
      formErrorLabels,
      pageType,
      notifications,
      ppeContent,
      priceFormatter,
    ]
  );

  useEffect(() => {
    pushToAppSettings({ pageType: pageType?.toUpperCase() });

    return () => {
      pushToAppSettings({ pageType: undefined });
    };
  }, [pageType]);

  return <StaticContext.Provider value={value}>{children}</StaticContext.Provider>;
};
